import React from 'react';
import Modal from '../ui/Modal';
import { X, Calendar, MapPin, FileText, Pencil, UserCheck } from 'lucide-react';
import { useLanguage } from '../../i18n/index.jsx';
import { getActiveTimezone } from '../../utils/timezone';

// ── Contact event VIEW mode (event sharing contact mode #305) ───────────────
//
// Clicking a shared event in a contact's Events section opens THIS — a
// read-only detail, never an edit form. Editing is a calendar concern and is
// only offered to the event's ORGANIZER, as a deep link into the calendar's own
// edit surface; an INVITEE gets no Edit affordance at all, because the event
// row belongs to the organizer's calendar and is not theirs to rewrite. That
// asymmetry is reciprocal by construction: each side computes `is_organizer`
// from their own invitation row, so the same event shows Edit to exactly one of
// the two people looking at it.
//
// PORTALED to <body> (the #225 pattern): the panels sit inside transformed
// ancestors, which become the containing block for `position: fixed` and would
// otherwise trap/clip this overlay inside the contact row.

const fmtDateTime = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    try {
        return d.toLocaleString(undefined, {
            weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit', timeZone: getActiveTimezone(),
        });
    } catch (_) {
        return d.toLocaleString();
    }
};

const Line = ({ Icon, children, testId }) => (
    <div data-testid={testId} className="flex items-start gap-2 text-sm text-pai-secondary">
        <Icon size={14} className="mt-0.5 text-pai-faint flex-shrink-0" />
        <span className="min-w-0 break-words">{children}</span>
    </div>
);

const ContactEventDetail = ({ event, contactName, onClose, onEdit }) => {
    const { t } = useLanguage();

    // <Modal>'s Escape is capture phase + stopPropagation — PanelShell also
    // closes on Escape, so without it, dismissing the detail would tear down
    // the whole Contacts panel behind it (the #272 lesson).

    if (!event) return null;

    const isOrganizer = !!event.is_organizer;
    const response = event.invitation_status || null;
    const responseLabel = response
        ? `${isOrganizer ? (contactName || t('contactEventGuest', 'Guest')) : t('contactEventYou', 'You')}: ${
            t(`contactEventStatus_${response}`, response)}`
        : null;

    const when = event.end_time
        ? `${fmtDateTime(event.start_time)} – ${fmtDateTime(event.end_time)}`
        : fmtDateTime(event.start_time);

    return (
        <Modal
            onClose={onClose}
            zIndex="z-[70]"
            cardClassName="max-h-[85dvh]"
            cardTestId="contact-event-detail"
            cardProps={{ 'data-organizer': isOrganizer ? 'true' : 'false' }}
        >
                <div className="flex items-start gap-2 px-4 py-3 border-b border-pai-subtle">
                    <div className="min-w-0 flex-1">
                        <div className="text-[10px] uppercase tracking-wide font-semibold text-pai-meta">
                            {t('contactEventViewTitle', 'Event')}
                        </div>
                        <h3 data-testid="contact-event-detail-title" className="text-sm font-semibold text-white break-words">
                            {event.title || t('contactEventUntitled', 'Untitled event')}
                        </h3>
                    </div>
                    <button
                        onClick={onClose}
                        aria-label={t('close', 'Close')}
                        className="p-1 text-pai-faint hover:text-white flex-shrink-0 transition-colors"
                    >
                        <X size={16} />
                    </button>
                </div>

                <div className="px-4 py-3 space-y-2">
                    <Line Icon={Calendar} testId="contact-event-when">{when}</Line>
                    {event.location && <Line Icon={MapPin} testId="contact-event-location">{event.location}</Line>}
                    {event.description && <Line Icon={FileText} testId="contact-event-description">{event.description}</Line>}
                    {responseLabel && <Line Icon={UserCheck} testId="contact-event-attendance">{responseLabel}</Line>}
                </div>

                <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-pai-subtle">
                    {/* ORGANIZER ONLY. An invitee is looking at someone else's
                        calendar row — read-only is the whole point. */}
                    {isOrganizer && (
                        <button
                            data-testid="contact-event-edit-btn"
                            onClick={() => onEdit?.(event)}
                            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-pai-secondary bg-white/[0.03] border border-pai hover:bg-white/[0.07] rounded-chip"
                        >
                            <Pencil size={12} /> {t('contactEventEdit', 'Edit')}
                        </button>
                    )}
                    <button
                        data-testid="contact-event-close"
                        onClick={onClose}
                        className="px-2.5 py-1.5 text-xs font-medium text-pai-secondary bg-white/[0.03] border border-pai hover:bg-white/[0.07] rounded-chip"
                    >
                        {t('close', 'Close')}
                    </button>
                </div>
            </Modal>
    );
};

export default ContactEventDetail;
